import { apiService } from '../../shared/services/apiService';
import { SpringPagination } from '../../shared/interfaces/SpringPagination';
import { ChallengeListFilters } from '../interfaces/challenges-list/ChallengeListFilters';
import { ChallengeRowInfo } from '../interfaces/challenges-list/ChallengeRowInfo';
import {
  ChallengeAboutSection,
  ChallengeFaqSection,
  ChallengeImportantDatesSection,
  ChallengePrizeSection,
  CreateChallengeInfo,
  SavedChallenge,
} from '../interfaces/SaveChallengeInfo';
import { DeepPartial } from '../../utils-types';
import { ChallengeProfileInfo } from '../interfaces/ChallengeProfileInfo';
import { ChallengeStatusDetails } from '../interfaces/ChallengeStatusDetails';
import { getFilteredTruthyFieldsObj } from '../../shared/utils/sortingAndGroupingUtil';

type GetChallengesListResponse = SpringPagination<ChallengeRowInfo[]>;

type GetChallengeRegistrationLinkResponse = {
  registrationLinkUrl: string;
  qrCodeUrl: string;
};

export const challengeService = {
  async getChallengesList(
    params: ChallengeListFilters,
  ): Promise<GetChallengesListResponse> {
    const filteredParams = getFilteredTruthyFieldsObj(params);

    const response = await apiService.post<GetChallengesListResponse>(
      '/api/challenges/filtered',
      filteredParams,
    );

    return response.data;
  },

  /** create challenge as draft, empty fileds should be null or not exist in payload */
  async createChallenge(data: DeepPartial<CreateChallengeInfo>): Promise<SavedChallenge> {
    const resp = await apiService.post<SavedChallenge>('api/challenges', data);
    return resp.data;
  },

  async publishChallenge(challengeId: number): Promise<SavedChallenge> {
    const resp = await apiService.post<SavedChallenge>(
      `api/challenges/${challengeId}/publish`,
    );
    return resp.data;
  },

  async unpublishChallenge(challengeId: number): Promise<SavedChallenge> {
    const resp = await apiService.post<SavedChallenge>(
      `api/challenges/${challengeId}/unpublish`,
    );
    return resp.data;
  },

  async getChallengeProfileInfo(challengeId: number) {
    const resp = await apiService.get<ChallengeProfileInfo>(
      `api/challenges/${challengeId}/common-info`,
    );

    return resp.data;
  },

  async getChallengeImportantDates(challengeId: number) {
    const resp = await apiService.get<ChallengeImportantDatesSection>(
      `api/challenges/${challengeId}/important-dates`,
    );

    return resp.data;
  },

  async updateChallengeImportantDates(
    challengeId: number,
    data: ChallengeImportantDatesSection,
  ) {
    const resp = await apiService.put<ChallengeImportantDatesSection>(
      `api/challenges/${challengeId}/important-dates`,
      data,
    );

    return resp.data;
  },

  async getChallengeAbout(challengeId: number) {
    const resp = await apiService.get<ChallengeAboutSection>(
      `api/challenges/${challengeId}/about`,
    );

    return resp.data;
  },

  async updateChallengeAbout(challengeId: number, data: ChallengeAboutSection) {
    const resp = await apiService.put<ChallengeAboutSection>(
      `api/challenges/${challengeId}/about`,
      data,
    );

    return resp.data;
  },

  async getChallengePrizes(challengeId: number) {
    const resp = await apiService.get<ChallengePrizeSection>(
      `api/challenges/${challengeId}/prizes`,
    );

    return resp.data;
  },

  async updateChallengePrizes(challengeId: number, data: ChallengePrizeSection) {
    const resp = await apiService.put<ChallengePrizeSection>(
      `api/challenges/${challengeId}/prizes`,
      data,
    );

    return resp.data;
  },

  async getChallengeFaqs(challengeId: number) {
    const resp = await apiService.get<ChallengeFaqSection>(
      `api/challenges/${challengeId}/faqs`,
    );

    return resp.data;
  },

  async updateChallengeFaqs(challengeId: number, data: ChallengeFaqSection) {
    const resp = await apiService.put<ChallengeFaqSection>(
      `api/challenges/${challengeId}/faqs`,
      data,
    );

    return resp.data;
  },

  async getChallengeStatusDetails(challengeId: number) {
    const resp = await apiService.get<ChallengeStatusDetails>(
      `api/challenges/${challengeId}/status-details`,
    );

    return resp.data;
  },

  async getChallengeRegistrationLink(challengeId: number) {
    const resp = await apiService.get<GetChallengeRegistrationLinkResponse>(
      `api/challenges/${challengeId}/registration-link`,
    );

    return resp.data;
  },
};
